import React, { useRef, useState } from "react";
import { theme } from "antd";
import { useTranslate } from "@refinedev/core";
import {
  CaretLeftIcon,
  PencilSimpleLineIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import { Button, Card, Popconfirm, Stack } from "@libertify/ui";
import {
  ADDON_CATALOG,
  CONFIG_ITEM,
  SETTINGS_ITEMS,
  AddonId,
} from "../data/addons";
import { usePlaybookDraft } from "../context/PlaybookDraftContext";
import { PlaybookEditModeProvider } from "../context/PlaybookEditModeContext";
import type { CarouselPlaybook } from "../types";
import { AddonRail } from "./AddonRail";
import { CONFIG_CARDS, type ConfigItemId } from "./config-cards/registry";
import {
  DescInput,
  PillGhostButton,
  PillPrimaryButton,
  EditNotice,
  EditNoticeIcon,
  HeadSub,
  Shell,
  TitleEditForm,
  TitleInput,
  TitleRow,
  Title,
  Body,
  RightCol,
  CardTitle,
  CardPlaceholder,
  EmptyBody,
} from "./CustomizePanel.styles";

const labelKeyFor = (id: ConfigItemId): string => {
  const settings = SETTINGS_ITEMS.find((s) => s.id === id);
  if (settings) return settings.labelKey;
  return ADDON_CATALOG.find((a) => a.id === id)?.labelKey ?? "";
};

interface CustomizePanelProps {
  playbook: CarouselPlaybook | null;
  /** Editing the playbook itself, rather than configuring an experience from
   *  it — the edits outlive this page, so the view has to say so. */
  isEditingPlaybook?: boolean;
  /** Persists the playbook's name and description. `null` clears the
   *  description. Only ever called in edit mode. */
  onSaveDetails?: (name: string, description: string | null) => Promise<void>;
  /** That save is in flight — owned by whoever runs the mutation. */
  isSavingDetails?: boolean;
  /** Return to the playbook carousel (clears the #customize hash). */
  onBack: () => void;
  /** The active rail item — lifted so the parent can jump to a tab (e.g.
   *  Documents) from outside the panel, the same way a rail click would. */
  active: ConfigItemId;
  onSelectActive: (id: ConfigItemId) => void;
}

/**
 * The #customize view: the shared add-on rail (Settings + the playbook's
 * add-ons, reorderable, with an "Add a new add-on" picker) and the active
 * item's config card on the right. All edits live in the shared PlaybookDraft
 * context.
 */
export const CustomizePanel: React.FC<CustomizePanelProps> = ({
  playbook,
  isEditingPlaybook = false,
  onSaveDetails,
  isSavingDetails = false,
  onBack,
  active,
  onSelectActive,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const {
    addonIds,
    addAddon,
    removeAddon,
    reorderAddons,
    addonsDirty,
    generalDirty,
  } = usePlaybookDraft();
  // Only playbook-edit mode can lose work on Back: the create flow's draft
  // survives in context, but here nothing is written until Save.
  const wouldDiscardEdits = isEditingPlaybook && (addonsDirty || generalDirty);

  // The name/description editor swaps places with the title, so the two are
  // never on screen together. The fields are uncontrolled — read on save —
  // and unmount with the form, so `defaultValue` reseeds them on each open.
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const [isNameEmpty, setIsNameEmpty] = useState(false);

  const openDetails = () => {
    setIsNameEmpty(!playbook?.name.trim());
    setIsEditingDetails(true);
  };

  const saveDetails = async () => {
    const name = nameRef.current?.value.trim() ?? "";
    if (!name || isSavingDetails) return;
    // Emptied on purpose means clear it, hence null over undefined.
    const description = descriptionRef.current?.value.trim() || null;
    await onSaveDetails?.(name, description);
    setIsEditingDetails(false);
  };

  const editNotice = React.useMemo(() => {
    const name = playbook?.name ?? "";
    const sentence = t("createExperience.customize.editingPlaybookNotice", {
      name,
    });
    const parts = name ? sentence.split(name) : [sentence];
    if (parts.length !== 2) return sentence;
    return (
      <>
        {parts[0]}
        <b>{name}</b>
        {parts[1]}
      </>
    );
  }, [playbook?.name, t]);

  // The active item's config card, if one is registered (else placeholder).
  const ActiveConfigCard = CONFIG_CARDS[active];

  const handleAdd = (ids: AddonId[]) => {
    ids.forEach(addAddon);
    if (ids[0]) onSelectActive(ids[0]);
  };

  const handleRemove = (id: AddonId) => {
    removeAddon(id);
    if (active === id) onSelectActive(CONFIG_ITEM.General);
  };

  return (
    <Shell $hasCreateBar={!isEditingPlaybook}>
      <Stack align="center" gap={16}>
        <Popconfirm
          title={t("createExperience.customize.discard.title")}
          description={t("createExperience.customize.discard.description")}
          confirmText={t("createExperience.customize.discard.confirm")}
          cancelText={t("createExperience.customize.discard.cancel")}
          onConfirm={onBack}
          disabled={!wouldDiscardEdits}
        >
          <Button
            variant="outlined"
            size="m"
            leftIcon={<CaretLeftIcon size={16} />}
            // With the Popconfirm disabled it never opens, so Back has to
            // leave on its own click.
            onClick={wouldDiscardEdits ? undefined : onBack}
          >
            {t("createExperience.customize.back")}
          </Button>
        </Popconfirm>
      </Stack>
      {isEditingPlaybook ? (
        <EditNotice role="status">
          <EditNoticeIcon>
            <WarningIcon size={18} weight="fill" />
          </EditNoticeIcon>
          <span>{editNotice}</span>
        </EditNotice>
      ) : null}
      {isEditingDetails ? (
        <TitleEditForm>
          <TitleInput
            autoFocus
            ref={nameRef}
            $token={token}
            defaultValue={playbook?.name ?? ""}
            placeholder={t(
              "createExperience.renamePlaybookModal.namePlaceholder"
            )}
            onInput={(event) =>
              setIsNameEmpty(!event.currentTarget.value.trim())
            }
            onKeyDown={(event) => {
              if (event.key === "Escape") setIsEditingDetails(false);
            }}
          />
          <DescInput
            ref={descriptionRef}
            $token={token}
            rows={3}
            defaultValue={playbook?.description ?? ""}
            placeholder={t(
              "createExperience.renamePlaybookModal.descriptionPlaceholder"
            )}
            onKeyDown={(event) => {
              if (event.key === "Escape") setIsEditingDetails(false);
            }}
          />
          <Stack align="center" gap={10}>
            <PillGhostButton
              $token={token}
              onClick={() => setIsEditingDetails(false)}
            >
              {t("createExperience.renamePlaybookModal.cancel")}
            </PillGhostButton>
            <PillPrimaryButton
              $token={token}
              disabled={isNameEmpty || isSavingDetails}
              loading={isSavingDetails}
              onClick={() => void saveDetails()}
            >
              {t("createExperience.renamePlaybookModal.save")}
            </PillPrimaryButton>
          </Stack>
        </TitleEditForm>
      ) : (
        <>
          <TitleRow>
            <Title $token={token}>
              {playbook?.name ?? t("createExperience.customize.customTitle")}
            </Title>
            {isEditingPlaybook ? (
              <PillGhostButton
                $token={token}
                icon={<PencilSimpleLineIcon size={15} weight="bold" />}
                onClick={openDetails}
              >
                {t("createExperience.customize.editDetails")}
              </PillGhostButton>
            ) : null}
          </TitleRow>
          {isEditingPlaybook && playbook?.description ? (
            <HeadSub $token={token}>{playbook.description}</HeadSub>
          ) : null}
        </>
      )}

      <Body>
        <AddonRail
          settingsItems={SETTINGS_ITEMS}
          addonIds={addonIds}
          active={active}
          onSelect={onSelectActive}
          onAddAddons={handleAdd}
          onReorderAddons={reorderAddons}
          rowAction={{ kind: "remove", onRemove: handleRemove }}
        />

        <RightCol>
          {ActiveConfigCard ? (
            <PlaybookEditModeProvider isEditingPlaybook={isEditingPlaybook}>
              <ActiveConfigCard />
            </PlaybookEditModeProvider>
          ) : (
            <Card>
              <CardTitle $token={token}>{t(labelKeyFor(active))}</CardTitle>
              <CardPlaceholder $token={token}>
                {t("createExperience.customize.placeholder")}
              </CardPlaceholder>
              <EmptyBody />
            </Card>
          )}
        </RightCol>
      </Body>
    </Shell>
  );
};
